import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { supabase } from "../../lib/supabase";
import Avatar from "../common/Avatar";

const CommentSection = ({
  postId,
  currentUser,
  onCommentChange,
  controlled = false,
  isOpen = false,
  showToggleButton = true,
  onCountChange,
}) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [newReply, setNewReply] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState({});
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:5000";

  // Logged-in user display name (Supabase)
  const supaMeta = currentUser?.user_metadata || {};
  const supaFirst = supaMeta.first_name || supaMeta.given_name || "";
  const supaLast = supaMeta.last_name || supaMeta.family_name || "";
  const supaComposed = [supaFirst, supaLast].filter(Boolean).join(" ");
  const supaName =
    supaComposed ||
    supaMeta.name ||
    supaMeta.full_name ||
    currentUser?.email ||
    "User";
  const supaAvatar = supaMeta.avatar_url || supaMeta.picture || "";

  // Display name for creator
  const getUserDisplayName = (creator) => {
    if (!creator) return "Anonymous";

    // Prioritize first_name from Supabase, then fallback to other sources
    if (creator.local?.firstName) {
      return creator.local.firstName;
    }

    // Fallback to other display name sources
    return (
      creator.local?.displayName ||
      creator.google?.displayName ||
      creator.facebook?.displayName ||
      `${creator.local?.firstName || ""} ${creator.local?.lastName || ""}`.trim() ||
      creator.local?.email ||
      creator.google?.email ||
      creator.facebook?.email ||
      "Anonymous"
    );
  };

  // Initial for avatar
  const getUserInitial = (creator) => {
    const displayName = getUserDisplayName(creator);
    return displayName !== "Anonymous" ? displayName[0]?.toUpperCase() : "U";
  };

  // Effective open state
  const open = controlled ? isOpen : showComments;

  // Sync when switching to controlled mode
  useEffect(() => {
    if (controlled) return; // unmanaged by parent
    // no-op here; state is local
  }, [controlled]);

  // Fetch comments when open changes
  useEffect(() => {
    if (open) {
      fetchComments();
    }
  }, [postId, open]);

  const fetchComments = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/api/gallery/posts/comment/${postId}`
      );
      const result = await response.json();

      const list = result?.data?.comments || result?.comments || [];
      if (result.success && Array.isArray(list)) {
        // Comments-only count (prefer server total)
        const commentsOnlyCount =
          typeof result?.data?.commentsCount === "number"
            ? result.data.commentsCount
            : list.length;
        // Token for replies endpoint (requires auth)
        let authHeaders = {};
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          const token =
            session?.access_token || localStorage.getItem("access_token");
          if (token) authHeaders.Authorization = `Bearer ${token}`;
        } catch (_) {}
        // Fetch replies per comment
        const commentsWithReplies = await Promise.all(
          list.map(async (comment) => {
            try {
              // If no token, skip replies to avoid 401
              if (!authHeaders.Authorization) {
                return { ...comment, replies: [] };
              }
              const repliesResponse = await fetch(
                `${baseUrl}/api/gallery/posts/reply/${comment._id}`,
                { headers: authHeaders }
              );
              const repliesResult = await repliesResponse.json();

              const repliesList =
                repliesResult?.data?.replies || repliesResult?.replies || [];
              return {
                ...comment,
                replies: repliesResult.success ? repliesList : [],
              };
            } catch (error) {
              console.error(
                "Error fetching replies for comment:",
                comment._id,
                error
              );
              return {
                ...comment,
                replies: [],
              };
            }
          })
        );

        setComments(commentsWithReplies);
        try {
          if (typeof onCountChange === "function")
            onCountChange(commentsOnlyCount);
        } catch (_) {}
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      toast.error("Failed to load comments");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    if (!currentUser) {
      toast.error("Please sign in to comment");
      return;
    }

    setIsSubmitting(true);

    try {
      // Token from Supabase session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token =
        session?.access_token || localStorage.getItem("access_token");

      const response = await fetch(
        `${baseUrl}/api/gallery/post/comment/${postId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            comment: newComment,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setNewComment("");
        await fetchComments();

        if (onCommentChange) {
          onCommentChange();
        }

        toast.success("Comment added successfully!");
      } else {
        throw new Error(result.message || "Failed to add comment");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();

    if (!newReply.trim()) {
      toast.error("Please enter a reply");
      return;
    }

    if (!currentUser) {
      toast.error("Please sign in to reply");
      return;
    }

    setIsSubmittingReply(true);

    try {
      // Token from Supabase session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token =
        session?.access_token || localStorage.getItem("access_token");

      const response = await fetch(
        `${baseUrl}/api/gallery/post/reply/${replyingTo}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reply: newReply,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setNewReply("");
        setReplyingTo(null);
        await fetchComments();

        toast.success("Reply added successfully!");
      } else {
        throw new Error(result.message || "Failed to add reply");
      }
    } catch (error) {
      console.error("Error adding reply:", error);
      toast.error("Failed to add reply. Please try again.");
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return moment(dateString).fromNow();
    } catch (error) {
      return "some time ago";
    }
  };

  const toggleReplies = (commentId) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const commentsCount =
    comments.length +
    comments.reduce(
      (total, comment) => total + (comment.replies?.length || 0),
      0
    );

  return (
    <>
      {/* Comments Toggle Button (optional) */}
      {showToggleButton && (
        <button
          onClick={() => setShowComments((prev) => !prev)}
          className="inline-flex items-center space-x-2 px-3 py-2 text-jacarta-500 hover:text-jacarta-700 hover:bg-jacarta-50 dark:hover:bg-jacarta-700 rounded-lg transition-colors"
        >
          <svg
            className="w-5 h-5"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 9H17M7 13H17M21 20L17.6757 18.3378C17.4237 18.2118 17.2977 18.1488 17.1656 18.1044C17.0484 18.065 16.9277 18.0365 16.8052 18.0193C16.6672 18 16.5263 18 16.2446 18H6.2C5.07989 18 4.51984 18 4.09202 17.782C3.71569 17.5903 3.40973 17.2843 3.21799 16.908C3 16.4802 3 15.9201 3 14.8V7.2C3 6.07989 3 5.51984 3.21799 5.09202C3.40973 4.71569 3.71569 4.40973 4.09202 4.21799C4.51984 4 5.0799 4 6.2 4H17.8C18.9201 4 19.4802 4 19.908 4.21799C20.2843 4.40973 20.5903 4.71569 20.782 5.09202C21 5.51984 21 6.0799 21 7.2V20Z"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="font-display font-medium leading-none">Comment</span>
          {commentsCount > 0 && (
            <span className="text-sm font-medium leading-none">
              ({commentsCount})
            </span>
          )}
        </button>
      )}

      {open && (
        <div className="mt-3 pt-3">
          {/* Comment Form */}
          {currentUser && (
            <form onSubmit={handleSubmitComment} className="mb-4">
              <div className="flex items-start space-x-3">
                <Avatar creator={null} size="md" nameFallback={supaName} />
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    className="w-full p-2 border border-jacarta-200 dark:border-jacarta-600 rounded-lg resize-none text-sm bg-white dark:bg-jacarta-700 text-jacarta-700 dark:text-white placeholder-jacarta-400"
                    rows={2}
                    maxLength={1000}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-jacarta-400">
                      {newComment.length}/1000
                    </span>
                    <button
                      type="submit"
                      disabled={isSubmitting || !newComment.trim()}
                      className="px-4 py-1 bg-accent text-white rounded-2lg text-sm hover:bg-accent-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-display font-semibold"
                    >
                      {isSubmitting ? "Posting..." : "Post"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}

          {/* Comments List */}
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent"></div>
            </div>
          ) : comments.length > 0 ? (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment._id} className="space-y-2">
                  {/* Main Comment */}
                  <div className="flex items-start space-x-3">
                    <Avatar creator={comment.creator} size="md" />
                    <div className="flex-1">
                      <div className="bg-jacarta-50 dark:bg-jacarta-700 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-display font-semibold text-sm text-jacarta-700 dark:text-white">
                            {getUserDisplayName(comment.creator)}
                          </span>
                          <span className="text-xs text-jacarta-400">
                            {formatDate(comment.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm text-jacarta-600 dark:text-jacarta-300">
                          {comment.comment}
                        </p>
                      </div>

                      <div className="flex items-center space-x-4 mt-2 ml-3">
                        <button
                          onClick={() =>
                            setReplyingTo(
                              replyingTo === comment._id ? null : comment._id
                            )
                          }
                          className="text-xs text-jacarta-500 hover:text-accent transition-colors"
                        >
                          Reply
                        </button>
                        {comment.replies && comment.replies.length > 0 && (
                          <button
                            onClick={() => toggleReplies(comment._id)}
                            className="text-xs text-jacarta-500 hover:text-accent transition-colors"
                          >
                            {expandedReplies[comment._id] ? "Hide" : "View"}{" "}
                            {comment.replies.length} repl
                            {comment.replies.length === 1 ? "y" : "ies"}
                          </button>
                        )}
                      </div>

                      {/* Reply Form */}
                      {replyingTo === comment._id && currentUser && (
                        <form
                          onSubmit={handleSubmitReply}
                          className="mt-3 ml-3"
                        >
                          <div className="flex items-start space-x-2">
                            <Avatar
                              creator={null}
                              size="sm"
                              nameFallback={supaName}
                            />
                            <div className="flex-1">
                              <textarea
                                value={newReply}
                                onChange={(e) => setNewReply(e.target.value)}
                                placeholder="Write a reply..."
                                className="w-full p-2 border border-jacarta-200 dark:border-jacarta-600 rounded-lg resize-none text-sm bg-white dark:bg-jacarta-700 text-jacarta-700 dark:text-white placeholder-jacarta-400"
                                rows={2}
                                maxLength={500}
                              />
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs text-jacarta-400">
                                  {newReply.length}/500
                                </span>
                                <div className="space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setReplyingTo(null);
                                      setNewReply("");
                                    }}
                                    className="px-3 py-1 text-xs text-jacarta-500 hover:text-jacarta-700 dark:hover:text-white transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="submit"
                                    disabled={
                                      isSubmittingReply || !newReply.trim()
                                    }
                                    className="px-3 py-1 bg-accent text-white rounded-2lg text-xs hover:bg-accent-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-display font-semibold"
                                  >
                                    {isSubmittingReply ? "Posting..." : "Reply"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </form>
                      )}

                      {/* Replies */}
                      {expandedReplies[comment._id] &&
                        comment.replies &&
                        comment.replies.length > 0 && (
                          <div className="ml-6 mt-3 space-y-3">
                            {comment.replies.map((reply) => (
                              <div
                                key={reply._id}
                                className="flex items-start space-x-2"
                              >
                                <Avatar creator={reply.creator} size="sm" />
                                <div className="flex-1">
                                  <div className="bg-jacarta-50 dark:bg-jacarta-700 rounded-lg p-2">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className="font-medium text-xs text-jacarta-700 dark:text-white">
                                        {getUserDisplayName(reply.creator)}
                                      </span>
                                      <span className="text-xs text-jacarta-400">
                                        {formatDate(reply.createdAt)}
                                      </span>
                                    </div>
                                    <p className="text-xs text-jacarta-600 dark:text-jacarta-300">
                                      {reply.reply}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-jacarta-400">
                {currentUser
                  ? "Be the first to comment!"
                  : "No comments yet. Sign in to join the conversation."}
              </p>
            </div>
          )}

          {!currentUser && (
            <div className="mt-4 p-3 bg-jacarta-50 dark:bg-jacarta-700 rounded-lg text-center">
              <p className="text-sm text-jacarta-500 dark:text-jacarta-400">
                Please sign in to join the conversation
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CommentSection;
